import { useCallback, useEffect, useMemo, useState } from 'react';
import { Incident, IncidentFilters } from '../types/Incidents';
import { HTTP_OPTIONS, INCIDENT_STATUS, INCIDENT_TYPE } from '@silverfort-nodelibs/common/utils/consts';
import { AxiosResponse } from 'axios';
import { Pageable } from '../../../../api/types/Pageable';
import { httpGet } from '../../../../components/utils';

const getCursorValueAsDbValue = (incident: Incident, sortField: string | undefined): string | number => {
    if (sortField === 'status') {
        return INCIDENT_STATUS[incident.status]?.value ?? 0;
    }
    if (sortField === 'kind') {
        return INCIDENT_TYPE[incident.kind]?.label ?? incident.kind;
    }
    return incident[sortField ?? 'id'];
};

const useGetIncidents = ({
    searchedIncidentId,
    pageSize,
    severities,
    statuses,
    kinds,
    sortField,
    sortDirection,
    showExcluded,
    fromLastLogTimestamp,
    mainEntitiesGuids
}: IncidentFilters) => {
    const [data, setData] = useState<Pageable<Incident[]>>({ pages: [] });
    const [isFetching, setIsFetching] = useState(false);
    const [hasNextPage, setHasNextPage] = useState(true);
    const [error, setError] = useState<string>();
    const [cursor, setCursor] = useState<{ value: string | number; id: string } | undefined>();

    const filters = useMemo(() => {
        return {
            searchedIncidentId,
            pageSize,
            severities,
            statuses,
            kinds,
            sortField,
            sortDirection,
            showExcluded,
            fromLastLogTimestamp,
            mainEntitiesGuids
        };
    }, [
        searchedIncidentId,
        pageSize,
        severities,
        statuses,
        kinds,
        sortField,
        sortDirection,
        showExcluded,
        fromLastLogTimestamp,
        mainEntitiesGuids
    ]);

    const getIncidentsFromServer = useCallback(
        async (requestParams: IncidentFilters) => {
            setIsFetching(true);
            setError(undefined);

            try {
                const result = (await httpGet('/incidents/', {
                    [HTTP_OPTIONS.REQ_OPTIONS]: {
                        params: requestParams
                    }
                })) as unknown as AxiosResponse<Incident[]>;

                const page = result.data;

                if (!page?.length) {
                    setHasNextPage(false);
                } else {
                    setData((prev) => ({ pages: [...prev.pages, page] }));

                    const lastItem = page[page.length - 1];
                    if (lastItem) {
                        setCursor({
                            value: getCursorValueAsDbValue(lastItem, filters.sortField),
                            id: lastItem.id
                        });
                    }
                }
            } catch (err) {
                setError('Failed to get incidents from server');
            }

            setIsFetching(false);
        },
        [filters.sortField]
    );

    const fetchNextPage = useCallback(async () => {
        if (!hasNextPage) return;

        await getIncidentsFromServer({
            ...filters,
            cursorValue: cursor?.value,
            cursorId: cursor?.id
        });
    }, [cursor, filters, hasNextPage, getIncidentsFromServer]);

    const refreshTable = useCallback(() => {
        setData({ pages: [] });
        setCursor(undefined);
        setHasNextPage(true);
        setIsFetching(false);
        setError(undefined);
        getIncidentsFromServer(filters);
    }, [filters, getIncidentsFromServer]);

    useEffect(() => {
        refreshTable();
    }, [refreshTable]);

    const mergedPages: Incident[] = useMemo(() => {
        return data.pages.flat();
    }, [data]);

    return { data: mergedPages, isFetching, error, fetchNextPage, refreshTable };
};

export default useGetIncidents;
